import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/AlarmProfiles/AssignmentsTable.jsx
import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    Button,
    CircularProgress
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Delete as UnlinkIcon,
    ManageAccounts as ManageIcon,
    Clear as ClearIcon,
    Star as StarIcon
} from '@mui/icons-material';

const AssignmentsTable = ({
    assignments,
    loading,
    profiles,
    searchTerm,
    onClearSearch,
    onViewProfile,
    onEditProfile,
    onDeleteAssignment,
    hasPermission,
    deletingAssignment
}) => {
  const { t } = useTranslation();
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{i18n.t('common.loading')}</Typography>
            </Box>
        );
    }

    if (assignments.length === 0) {
        return (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
                <ManageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('hardcoded.components_alarmprofiles_assignmentstable.no_se_encontraron_asignaciones')}</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                    {searchTerm
                        ? t('hardcoded.components_alarmprofiles_assignmentstable.no_hay_asignaciones_que_coincidan', { searchTerm: searchTerm })
                        : t('hardcoded.components_alarmprofiles_assignmentstable.asigna_perfiles_a_vehiculos_desde')}
                </Typography>
                {searchTerm && (
                    <Button variant="outlined" onClick={onClearSearch} startIcon={<ClearIcon />}>
                        {i18n.t('common.clear')}</Button>
                )}
            </Paper>
        );
    }

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {t('vehicles.table.vehicle')}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {t('navbar.profile')}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {t('hardcoded.components_alarmprofiles_assignmentstable.configuraciones')}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                                Fecha
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                                {i18n.t('gps.table.status')}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                                {t('common.actions')}</Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {assignments.map((assignment) => {
                        const assignedDate = new Date(assignment.assigned_at).toLocaleDateString();
                        const profile =
                            assignment.profile ||
                            profiles?.find((p) => String(p.id) === String(assignment.profile_id));
                        const configCount = profile?.configurations?.length || 0;

                        return (
                            <TableRow key={assignment.id} hover sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                                <TableCell>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {assignment.vehicle_name}
                                    </Typography>
                                </TableCell>

                                <TableCell>
                                    <Box
                                        sx={{ cursor: profile ? 'pointer' : 'default' }}
                                        onClick={() => profile && onViewProfile(profile)}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            sx={{ textDecoration: profile ? 'underline' : 'none' }}
                                        >
                                            {assignment.profile_name || profile?.name || '—'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {profile?.axle_count} {i18n.t('hardcoded.components_alarmprofiles_assignmentstable.ejes')}{' '}
                                            {profile?.applies_to_all_axles ? t('hardcoded.components_alarmprofiles_assignmentstable.todos') : t('hardcoded.components_alarmprofiles_assignmentstable.especifico')}
                                        </Typography>
                                    </Box>
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2">{configCount} {t('hardcoded.components_alarmprofiles_assignmentstable.configuraciones')}</Typography>
                                    {profile?.metrics_configured && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            {profile.metrics_configured.slice(0, 3).map((metric) => (
                                                <Chip
                                                    key={metric}
                                                    label={metric}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                />
                                            ))}
                                            {profile.metrics_configured.length > 3 && (
                                                <Chip
                                                    label={`+${profile.metrics_configured.length - 3}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </TableCell>

                                <TableCell align="center">
                                    <Typography variant="body2">{assignedDate}</Typography>
                                </TableCell>

                                <TableCell align="center">
                                    <Chip
                                        label={assignment.active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                                        size="small"
                                        color={assignment.active ? 'success' : 'default'}
                                        variant={assignment.active ? 'filled' : 'outlined'}
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                        <Tooltip title={t('hardcoded.components_alarmprofiles_assignmentstable.ver_detalle_del_perfil')}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => profile && onViewProfile(profile)}
                                                    disabled={!profile}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>

                                        {hasPermission('manager') && (
                                            <Tooltip title={t('hardcoded.components_alarmprofiles_assignmentstable.editar_perfil')}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => profile && onEditProfile(profile)}
                                                        disabled={!profile}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}

                                        {hasPermission('manager') && (
                                            <Tooltip title={t('alarmProfiles.removeAssignment')}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => onDeleteAssignment(assignment)}
                                                    disabled={deletingAssignment}
                                                >
                                                    <UnlinkIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default AssignmentsTable;